import React, { lazy, Suspense, useMemo, useState, useEffect, useRef } from "react"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import { Link, useNavigate } from "@tanstack/react-router"
import {
  ArrowRight,
  MapPin,
  Users,
  Sparkles,
  CalendarDays,
  MessageSquare,
} from "lucide-react"
import { TravelPartners } from "@/components/TravelPartners"
import { Button } from "@/components/ui/button"
import { tripSlug } from "@/lib/slugify"
import { authClient } from "@/lib/auth-client"
import { trpc } from "@/lib/trpc-client"
import { trackEvent } from "@/components/Analytics"
import { DASHBOARD_KEY } from "@/lib/hooks/use-dashboard"
import { useGuideLikes } from "@/lib/hooks/use-guide-likes"
import { SITE_DESCRIPTION, SITE_NAME, SITE_URL, absoluteUrl } from "@/lib/seo"

// Below-fold + embla-backed, so it's lazy-loaded to keep the Carousel engine and
// ExploreCard's Radix off the home page's first wave (the hero is the LCP).
const FeaturedGuidesCarousel = lazy(
  () => import(`@/components/FeaturedGuidesCarousel`)
)

type CopyingGuide = {
  id: string
  name?: string | null
  destination: string
  image_url?: string | null
}

function AppScreenshotPlaceholder({
  label,
  aspect = `aspect-[16/10]`,
}: {
  label: string
  aspect?: string
}) {
  return (
    <div
      className={`${aspect} w-full rounded-2xl border-2 border-dashed border-border bg-muted flex flex-col items-center justify-center gap-2 text-center px-4`}
    >
      <div className="w-8 h-8 rounded-lg bg-primary/20 flex items-center justify-center">
        <span className="text-primary text-lg">📱</span>
      </div>
      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide leading-snug max-w-[180px]">
        {label}
      </p>
    </div>
  )
}

// App screenshot that swaps to a portrait mobile capture below the `sm`
// breakpoint (640px) — matching the product-demo videos further down the page.
// A <picture> means the browser only downloads the image it actually renders,
// and the per-source width/height reserve the right aspect ratio (no layout
// shift) for whichever viewport is showing. Pass mobile={null} when a view has
// no phone equivalent (e.g. the desktop-only map) to render the desktop shot
// everywhere.
function ResponsiveShot({
  desktop,
  mobile,
  alt,
  priority = false,
}: {
  desktop: string
  mobile?: string | null
  alt: string
  priority?: boolean
}) {
  const loadProps = priority
    ? ({ fetchPriority: `high` } as const)
    : ({ loading: `lazy` } as const)
  // CRITICAL for CLS/LCP: the <img> width/height (1440×900, landscape) only
  // describe the DESKTOP source. On mobile the browser loads the PORTRAIT
  // mobile shot (393×851), so a single declared aspect reserves the wrong box
  // and the image reflows on load — a huge layout shift that also delayed LCP.
  // Reserve the actual aspect per breakpoint via CSS (overrides the attr ratio):
  // portrait below `sm`, landscape at `sm`+. Shots with no mobile variant stay
  // landscape everywhere.
  const aspectClass = mobile
    ? `aspect-[393/851] sm:aspect-[1440/900]`
    : `aspect-[1440/900]`
  const img = (
    <img
      src={desktop}
      alt={alt}
      width={1440}
      height={900}
      decoding="async"
      className={`w-full block object-cover ${aspectClass}`}
      {...loadProps}
    />
  )
  // Derive a WebP path for any PNG/JPG /screenshots/ asset we control so the
  // browser can pick the smaller format when it supports it. External URLs pass
  // through unchanged.
  const toWebp = (path: string) =>
    path.startsWith(`/screenshots/`) && path.endsWith(`.png`)
      ? path.replace(/\.png$/, `.webp`)
      : null
  const desktopWebp = toWebp(desktop)
  const mobileWebp = mobile ? toWebp(mobile) : null

  if (!mobile) {
    if (!desktopWebp) return img
    return (
      <picture>
        <source type="image/webp" srcSet={desktopWebp} width={1440} height={900} />
        {img}
      </picture>
    )
  }
  return (
    <picture>
      {/* WebP sources first (desktop then mobile) so WebP-capable browsers pick
          the smaller file at each breakpoint; the PNG <source>/<img> are the
          fallback for the rest. */}
      {desktopWebp && (
        <source
          type="image/webp"
          media="(min-width: 640px)"
          srcSet={desktopWebp}
          width={1440}
          height={900}
        />
      )}
      {mobileWebp && (
        <source
          type="image/webp"
          media="(max-width: 639px)"
          srcSet={mobileWebp}
          width={393}
          height={851}
        />
      )}
      <source media="(max-width: 639px)" srcSet={mobile} width={393} height={851} />
      {img}
    </picture>
  )
}

const features: {
  icon: React.ReactNode
  title: string
  body: string
  screenshot: string | null
  screenshotMobile?: string | null
  placeholder?: string
}[] = [
  {
    icon: <Sparkles className="h-5 w-5" />,
    title: `Smart Planning`,
    body: `Chat with Chic to build a full itinerary in seconds. It learns your preferences and refines suggestions as you go.`,
    screenshot: `/screenshots/trip-chat.png`,
    screenshotMobile: `/screenshots/trip-chat-mobile.png`,
  },
  {
    icon: <CalendarDays className="h-5 w-5" />,
    title: `Day-by-Day Itineraries`,
    body: `Drag, drop, and reorder activities across days. Your whole trip organized exactly how you want it.`,
    screenshot: `/screenshots/trip-itinerary.png`,
    screenshotMobile: `/screenshots/trip-itinerary-mobile.png`,
  },
  {
    icon: <MapPin className="h-5 w-5" />,
    title: `Interactive Map View`,
    body: `See every place on a live map. Spot clusters, find what's nearby, and optimize your routes without leaving the app.`,
    // Map is a desktop-only view — show the desktop shot on every device.
    screenshot: `/screenshots/trip-map.png`,
    screenshotMobile: null,
  },
  {
    icon: <Users className="h-5 w-5" />,
    title: `Real-Time Collaboration`,
    body: `Invite your travel crew and plan together. Everyone sees changes instantly — no more email threads or shared docs.`,
    screenshot: `/screenshots/dashboard.png`,
    screenshotMobile: `/screenshots/dashboard-mobile.png`,
  },
]

const steps = [
  {
    number: `01`,
    title: `Tell us where you're going`,
    body: `Enter your destination, dates, and travel style — or just describe your dream trip in plain language.`,
  },
  {
    number: `02`,
    title: `Get an instant itinerary`,
    body: `Chic builds a day-by-day plan with places, restaurants, and activities tailored to you.`,
  },
  {
    number: `03`,
    title: `Tweak until it's perfect`,
    body: `Swap places, reorder days, add notes, and pin favorites. Full control, zero friction.`,
  },
  {
    number: `04`,
    title: `Go explore`,
    body: `Travel with your plan in your pocket. Everything synced, offline-ready, and shareable with your crew.`,
  },
]

function LazyVideo({
  className,
  poster,
  webm,
  mp4,
  width,
  height,
  "aria-label": ariaLabel,
}: {
  className?: string
  poster: string
  webm: string
  mp4: string
  width: number
  height: number
  "aria-label"?: string
}) {
  const ref = useRef<HTMLDivElement>(null)
  const [inView, setInView] = useState(false)

  useEffect(() => {
    const el = ref.current
    if (!el) return
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setInView(true); obs.disconnect() } },
      { rootMargin: `200px` }
    )
    obs.observe(el)
    return () => obs.disconnect()
  }, [])

  return (
    <div ref={ref} className={className} style={{ aspectRatio: `${width}/${height}` }}>
      {inView ? (
        <video
          className="w-full h-full"
          autoPlay
          loop
          muted
          playsInline
          preload="none"
          poster={poster}
          width={width}
          height={height}
          aria-label={ariaLabel}
        >
          <source src={webm} type="video/webm" />
          <source src={mp4} type="video/mp4" />
        </video>
      ) : (
        <img src={poster} width={width} height={height} className="w-full h-full object-cover" alt="" aria-hidden />
      )}
    </div>
  )
}

// Static placeholder shared by the guides-loading state and the lazy carousel's
// Suspense fallback. Module-level so it isn't re-created each render.
const guidesSkeleton = (
  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
    {[0, 1, 2, 3].map((item) => (
      <div key={item} className="space-y-3">
        <div className="aspect-[3/4] rounded-lg bg-muted animate-pulse" />
        <div className="h-4 w-3/4 rounded bg-muted animate-pulse" />
        <div className="h-3 w-1/2 rounded bg-muted animate-pulse" />
      </div>
    ))}
  </div>
)

export function HomePage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  // The page is SSR'd so the session is null on the server and resolves on
  // hydration — same pattern the Header uses. We branch the hero/CTA on it so a
  // logged-in visitor isn't told to "sign up" / "sign in" with their avatar
  // already in the header.
  const { data: authSession } = authClient.useSession()
  const isAuthed = Boolean(authSession?.user)
  const { data: guides = [], isLoading: isGuidesLoading } = useQuery({
    queryKey: [`guides`, `public`],
    queryFn: () => trpc.guides.listPublic.query(),
  })
  const { likedGuideIds, handleLikeGuide, handleUnlikeGuide } = useGuideLikes()
  const [copyingGuide, setCopyingGuide] = useState<CopyingGuide | null>(null)

  const featuredGuides = useMemo(
    () =>
      [...guides]
        .sort(
          (a, b) =>
            new Date(b.created_at as unknown as string).getTime() -
            new Date(a.created_at as unknown as string).getTime()
        )
        .slice(0, 10),
    [guides]
  )

  const handleCopyGuide = async (guideId: string) => {
    // Copying writes a trip, so it needs an account. Send signed-out visitors to
    // sign up (the landing page's funnel) instead of letting the mutation fail.
    if (!isAuthed) {
      navigate({ to: `/sign-up` })
      return
    }
    const guide = guides.find((g) => g.id === guideId)
    if (!guide) return
    try {
      setCopyingGuide({
        id: guide.id,
        name: guide.name,
        destination: guide.destination,
        image_url: guide.image_url,
      })
      const result = await trpc.guides.duplicate.mutate({ id: guideId })
      // The viral loop: discovery → copy → a new editable trip (activation).
      trackEvent(`guide_duplicated`, { guide_id: guideId, source: `home` })
      // Refresh the dashboard cache so the header can find the new trip
      // (it reads the trip from DASHBOARD_KEY, not getTripData).
      await queryClient.invalidateQueries({ queryKey: DASHBOARD_KEY })
      navigate({
        to: `/trip/$slug/$tripId`,
        params: { slug: tripSlug(result.item), tripId: result.item.short_id },
      })
    } catch (err: unknown) {
      // Only an auth failure means "go log in" — other errors (network, quota)
      // shouldn't kick an authenticated user out to the login page.
      setCopyingGuide(null)
      const status = (err as { data?: { httpStatus?: number } })?.data
        ?.httpStatus
      if (status === 401 || status === 403) {
        navigate({ to: `/login` })
      }
    }
  }

  return (
    <main className="min-h-screen bg-background">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify([
            {
              "@context": `https://schema.org`,
              "@type": `WebApplication`,
              name: SITE_NAME,
              description: SITE_DESCRIPTION,
              url: SITE_URL,
              applicationCategory: `TravelApplication`,
              operatingSystem: `Web`,
              image: absoluteUrl(`/screenshots/trip-itinerary.png`),
              offers: {
                "@type": `Offer`,
                price: `0`,
                priceCurrency: `USD`,
              },
            },
            {
              "@context": `https://schema.org`,
              "@type": `WebSite`,
              name: SITE_NAME,
              url: SITE_URL,
            },
          ]),
        }}
      />
      {/* Decorative motion is now pure CSS (was framer-motion, which dragged the
          vendor-motion chunk onto the home page's first wave). The hero paints
          static for the fastest LCP; below-fold sections fade up via a
          scroll-driven `view()` timeline — zero JS, no main-thread cost at load.
          Browsers without scroll-timeline support (and reduced-motion users) just
          get the final, fully-visible state: `animation` with no duration settles
          instantly on the `to` frame, so no content is ever hidden behind JS. */}
      <style>{`
        @keyframes float-card { 0%,100% { transform: translateY(0px); } 50% { transform: translateY(-6px); } }
        @keyframes float-card-alt { 0%,100% { transform: translateY(0px); } 50% { transform: translateY(6px); } }
        @keyframes reveal-up { from { opacity: 0; transform: translateY(28px); } to { opacity: 1; transform: none; } }
        @media (prefers-reduced-motion: no-preference) {
          .reveal { animation: reveal-up linear both; animation-timeline: view(); animation-range: entry 0% entry 45%; }
        }
      `}</style>

      {/* ── Hero ──────────────────────────────────────────────────────── */}
      <section className="max-w-6xl mx-auto px-6 pt-10 pb-10 md:pt-14 md:pb-14">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-12 items-center">
          {/* Left: copy. Holds the LCP <h1> — rendered fully visible and static
              (no entrance animation) for the fastest possible mobile LCP. This
              used to be a framer-motion stagger; the chunk it pulled in was the
              home page's perf cap, and an opacity:0 SSR state hid the headline
              behind JS. */}
          <div className="flex flex-col gap-5">
            {[
              <h1 className="text-4xl sm:text-5xl md:text-6xl font-extrabold leading-[1.08] tracking-tight">
                Don't Plan Trips Alone.
                <br />
                <span className="text-primary">Chic's with you.</span>
              </h1>,
              <p className="text-muted-foreground text-base md:text-lg leading-relaxed max-w-sm md:max-w-md">
                Trip planning is overwhelming. Chic, your{` `}
                {/* Keyword-anchored internal link — the SEO entry point for the
                    "ai trip planner" query family lives on its own page. */}
                <Link
                  to="/ai-trip-planner"
                  preload="intent"
                  className="underline decoration-primary/40 underline-offset-4 hover:text-foreground transition-colors"
                >
                  AI trip planner
                </Link>
                {` `}copilot, is always there to draft itineraries, sort
                logistics, and help with every detail, while you stay in full
                control.
              </p>,
              <div className="flex flex-col gap-3 w-full">
                <div className="flex items-center gap-3 flex-wrap">
                  {isAuthed ? (
                    <Button
                      size="lg"
                      className="rounded-full px-8 h-12 text-base font-semibold shadow-md gap-2"
                      onClick={() => navigate({ to: `/dashboard` })}
                    >
                      Go to your trips <ArrowRight className="h-4 w-4" />
                    </Button>
                  ) : (
                    <>
                      <Button
                        size="lg"
                        className="rounded-full px-8 h-12 text-base font-semibold shadow-md gap-2"
                        onClick={() => navigate({ to: `/sign-up` })}
                      >
                        Start planning free <ArrowRight className="h-4 w-4" />
                      </Button>
                      <Button
                        size="lg"
                        variant="outline"
                        className="rounded-full px-8 h-12 text-base font-medium"
                        onClick={() => navigate({ to: `/login` })}
                      >
                        Sign in
                      </Button>
                    </>
                  )}
                </div>
                {!isAuthed && (
                  <p className="text-xs text-muted-foreground">
                    No credit card required
                  </p>
                )}
              </div>,
              <TravelPartners />,
            ].map((child, i) => (
              <div key={i}>{child}</div>
            ))}
          </div>

          {/* Right: hero app screenshot. On phones the screenshot is a portrait
              mobile capture, so cap the whole cluster (image + floating cards)
              to a phone-mockup width and center it. */}
          {/* This cluster contains the hero screenshot, the LCP element on most
              viewports — so it paints at full opacity immediately (no entrance
              animation gating it behind JS). The floating cards keep their idle
              CSS float keyframes. */}
          <div className="relative max-sm:max-w-[280px] max-sm:mx-auto">
            <div className="w-full rounded-2xl overflow-hidden shadow-2xl border border-border">
              <ResponsiveShot
                desktop="/screenshots/trip-itinerary.png"
                mobile="/screenshots/trip-itinerary-mobile.png"
                alt="Trip itinerary view"
                priority
              />
            </div>
            {/* Floating card: AI chat bubble */}
            <div
              className="absolute -bottom-4 -left-6 bg-card border border-border rounded-2xl shadow-xl px-4 py-3 flex items-center gap-3 max-w-[220px]"
              style={{ animation: `float-card 4s ease-in-out infinite` }}
            >
              <MessageSquare className="h-5 w-5 text-primary shrink-0" />
              <p className="text-xs font-medium leading-snug">
                "Plan 5 days in Kyoto for foodies"
              </p>
            </div>
            {/* Floating card: collab avatars */}
            <div
              className="absolute -top-4 -right-4 bg-card border border-border rounded-2xl shadow-xl px-4 py-3 flex items-center gap-2"
              style={{ animation: `float-card-alt 4.5s ease-in-out infinite` }}
            >
              <div className="flex -space-x-2">
                {[`bg-primary`, `bg-secondary`, `bg-amber-400`].map((c, i) => (
                  <div
                    key={i}
                    className={`h-7 w-7 rounded-full ${c} border-2 border-card`}
                  />
                ))}
              </div>
              <p className="text-xs font-medium">3 planning together</p>
            </div>
          </div>
        </div>
      </section>

      {/* ── Featured guides ──────────────────────────────────────────── */}
      <section className="px-6 pb-14 md:pb-16">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
            <div>
              <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-2">
                Community guides
              </p>
              <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight">
                Start from a guide
              </h2>
            </div>
            <Button
              variant="outline"
              className="rounded-full gap-2 w-fit"
              onClick={() => navigate({ to: `/trips/explore` })}
            >
              Explore all <ArrowRight className="h-4 w-4" />
            </Button>
          </div>

          {isGuidesLoading ? (
            guidesSkeleton
          ) : featuredGuides.length > 0 ? (
            // Suspense fallback is the same skeleton so the lazy carousel chunk
            // streaming in is indistinguishable from the data still loading.
            <Suspense fallback={guidesSkeleton}>
              <FeaturedGuidesCarousel
                guides={featuredGuides}
                likedGuideIds={likedGuideIds}
                copyingGuideId={copyingGuide?.id ?? null}
                onCopy={handleCopyGuide}
                onLike={handleLikeGuide}
                onUnlike={handleUnlikeGuide}
              />
            </Suspense>
          ) : (
            <div className="text-center py-12 border border-dashed border-border rounded-2xl">
              <h3 className="text-base font-bold mb-1">
                No published guides yet
              </h3>
              <p className="text-muted-foreground text-sm">
                Check back soon for community itineraries.
              </p>
            </div>
          )}
        </div>
      </section>

      {/* ── Product showcase video ─────────────────────────────────────── */}
      <section className="px-6 pb-16 md:pb-24 overflow-hidden">
        <div className="reveal max-w-6xl mx-auto text-center">
          <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-2">
            See it in action
          </p>
          <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight mb-8">
            Your whole trip, in one place
          </h2>
          {/* Device-framed product demos. Videos are lazy-mounted via
              IntersectionObserver so the browser never fetches the video data
              on initial page load (autoPlay ignores preload="none" in most
              engines). Poster images are WebP to minimise the placeholder cost.
              width/height reserve the correct aspect ratio to prevent CLS. */}
          <LazyVideo
            className="sm:hidden block w-full h-auto mx-auto max-w-xs"
            poster="/demo/demo-tokyo-anime-mobile-poster.webp"
            webm="/demo/demo-tokyo-anime-mobile.webm"
            mp4="/demo/demo-tokyo-anime-mobile.mp4"
            width={466}
            height={924}
            aria-label="ChicTravel trip planning on mobile"
          />
          <LazyVideo
            className="hidden sm:block w-full h-auto"
            poster="/demo/demo-tokyo-anime-poster.webp"
            webm="/demo/demo-tokyo-anime.webm"
            mp4="/demo/demo-tokyo-anime.mp4"
            width={1528}
            height={1028}
            aria-label="ChicTravel trip planning on desktop"
          />
        </div>
      </section>

      {/* ── Features ──────────────────────────────────────────────────── */}
      <section className="py-16 px-6">
        <div className="max-w-6xl mx-auto flex flex-col gap-16">
          {features.map((f, i) => {
            const isEven = i % 2 === 0
            // Portrait mobile captures get capped to a phone-mockup width on
            // phones; the desktop-only map shot stays full width.
            const imageBlock = (
              <div
                className={`w-full max-w-lg mx-auto lg:max-w-none rounded-2xl overflow-hidden shadow-xl border border-border ${
                  f.screenshotMobile ? `max-sm:max-w-[280px]` : ``
                }`}
              >
                {f.screenshot ? (
                  <ResponsiveShot
                    desktop={f.screenshot}
                    mobile={f.screenshotMobile}
                    alt={f.title}
                  />
                ) : (
                  <AppScreenshotPlaceholder
                    label={f.placeholder!}
                    aspect="aspect-[16/9]"
                  />
                )}
              </div>
            )
            const copyBlock = (
              <div className="flex flex-col justify-center gap-5">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center shrink-0">
                    {f.icon}
                  </div>
                  <h3 className="text-3xl font-extrabold tracking-tight leading-snug">
                    {f.title}
                  </h3>
                </div>
                <p className="text-muted-foreground text-lg leading-relaxed">
                  {f.body}
                </p>
              </div>
            )
            return (
              <div
                key={f.title}
                className="reveal grid grid-cols-1 lg:grid-cols-2 gap-12 items-center"
              >
                <div className={isEven ? `lg:order-last` : ``}>{copyBlock}</div>
                <div className={isEven ? `lg:order-first` : ``}>
                  {imageBlock}
                </div>
              </div>
            )
          })}
        </div>
      </section>

      {/* ── How it works ──────────────────────────────────────────────── */}
      <section className="py-16 px-6 bg-card border-y border-border">
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-3">
              How it works
            </p>
            <h2 className="text-4xl font-extrabold tracking-tight mb-8">
              From idea to itinerary
              <br />
              in four steps
            </h2>
            <div className="flex flex-col gap-6">
              {steps.map((s) => (
                <div key={s.number} className="reveal flex gap-5 items-start">
                  <span className="text-3xl font-black text-primary/20 leading-none w-10 shrink-0">
                    {s.number}
                  </span>
                  <div>
                    <p className="font-bold text-sm mb-1">{s.title}</p>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {s.body}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="w-full rounded-2xl overflow-hidden shadow-xl border border-border">
            <ResponsiveShot
              desktop="/screenshots/trip-map.png"
              mobile={null}
              alt="Trip planning in action"
            />
          </div>
        </div>
      </section>

      {/* ── Final CTA ─────────────────────────────────────────────────── */}
      <section className="py-16 px-6 bg-primary">
        <div className="reveal max-w-2xl mx-auto text-center">
          <h2 className="text-4xl font-extrabold tracking-tight text-primary-foreground mb-4">
            Your next adventure starts here
          </h2>
          <p className="text-primary-foreground/80 text-base mb-10">
            Join thousands of travelers planning smarter, exploring more, and
            stressing less.
          </p>
          <div className="flex gap-3 justify-center flex-wrap">
            <Button
              size="lg"
              variant="secondary"
              className="rounded-full px-8 h-12 font-semibold shadow-md gap-2 bg-white text-primary hover:bg-white/90"
              onClick={() => navigate({ to: isAuthed ? `/dashboard` : `/sign-up` })}
            >
              {isAuthed ? `Go to your trips` : `Start planning free`}{` `}
              <ArrowRight className="h-4 w-4" />
            </Button>
          </div>
          {!isAuthed && (
            <p className="text-primary-foreground/60 text-xs mt-4">
              No credit card required · Free forever on basic
            </p>
          )}
        </div>
      </section>
    </main>
  )
}
